import { useEffect, useState, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { CheckCircle2, XCircle, Loader2, Crown } from "lucide-react";

export function PaymentSuccess() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { checkAuth } = useAuth();
  const [state, setState] = useState("checking"); // checking | paid | timeout
  const [lookup, setLookup] = useState("");
  const attempts = useRef(0);

  useEffect(() => {
    const sessionId = params.get("session_id");
    if (!sessionId) { setState("timeout"); return; }
    let stop = false;
    const poll = async () => {
      if (stop) return;
      attempts.current += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        setLookup(data.lookup_key || "");
        if (data.payment_status === "paid") {
          setState("paid");
          await checkAuth();
          return;
        }
      } catch (err) { console.warn("Payment status poll attempt failed, retrying:", err); }
      if (attempts.current >= 8) { setState("timeout"); return; }
      setTimeout(poll, 2000);
    };
    poll();
    return () => { stop = true; };
  }, [params, checkAuth]);

  const isSupporter = lookup.startsWith("supporter");

  return (
    <div className="mx-auto max-w-lg px-4 py-24 text-center" data-testid="payment-success">
      {state === "checking" && (<><Loader2 className="mx-auto animate-spin text-[#00F0FF]" size={36} /><p className="mt-4 text-[#94A3B8]">Confirming your payment…</p></>)}
      {state === "paid" && (
        <>
          {isSupporter ? <Crown className="mx-auto text-[#FFB800]" size={44} /> : <CheckCircle2 className="mx-auto text-[#00FF87]" size={44} />}
          <h1 className="mt-4 font-heading text-3xl font-black text-white">{isSupporter ? "Welcome, Supporter!" : "Thank you!"}</h1>
          <p className="mt-2 text-[#94A3B8]">{isSupporter ? "Your Supporter badge and perks are now active." : "Your tip keeps the community wall running. Much appreciated."}</p>
          <Button onClick={() => navigate("/")} className="mt-6 bg-[#00F0FF] font-bold text-[#090C15] hover:bg-[#00F0FF]/90">Back to the Wall</Button>
        </>
      )}
      {state === "timeout" && (
        <>
          <Loader2 className="mx-auto text-[#FFB800]" size={40} />
          <h1 className="mt-4 font-heading text-2xl font-bold text-white">Still processing…</h1>
          <p className="mt-2 text-[#94A3B8]">Your payment may take a moment. Check your Supporter status shortly.</p>
          <Button onClick={() => navigate("/supporter")} variant="outline" className="mt-6 border-[#1E293F] bg-[#141B2D] text-white hover:bg-[#1A233A] hover:text-white">Go to Supporter page</Button>
        </>
      )}
    </div>
  );
}

export function PaymentCancel() {
  const navigate = useNavigate();
  return (
    <div className="mx-auto max-w-lg px-4 py-24 text-center" data-testid="payment-cancel">
      <XCircle className="mx-auto text-[#FF2A55]" size={44} />
      <h1 className="mt-4 font-heading text-2xl font-bold text-white">Checkout canceled</h1>
      <p className="mt-2 text-[#94A3B8]">No charge was made. You can support the wall anytime.</p>
      <Button onClick={() => navigate("/supporter")} className="mt-6 bg-[#FFB800] font-bold text-[#090C15] hover:bg-[#FFB800]/90">Back to Supporter</Button>
    </div>
  );
}
